import clsx from "clsx";
import { MagnifierPlus, Xmark } from "@gravity-ui/icons";
import GlassPanel from "./GlassPanel";
import LightboxButton from "./LightboxButton";
import SlideMount from "./SlideMount";
import type { CaseView } from "./caseView";
import { productFor } from "./caseProduct";
import type { StoryMode } from "./useStoryMode";
import styles from "./CaseChapter.module.scss";

/* UI-DESIGN: LB-14 one chapter of the scroll story per case: the slide on
   the light and a glass card with what a host decides on (players, length,
   the tagline) and the one next step. In the live scene the slide is drawn
   by three.js where the empty stage is; in the static stack it is the CSS
   mount; on phones it is a pre-rendered frame of the same scene (LB-16).
   LB-18: one side for every chapter (card left, slide right); the
   slides travel, the reading position stays put.
   LB-32: chapters stop repeating: every other case swaps sides (card
   right, slide left), in step with `flipped` in StoryScene.
   LB-45 the card reads like a shop item without shouting: name, a one-line
   hook, the quiet facts on the evidence tag and one action, Preview the
   first clue. Hosting moves into that overlay. The cover art is the slide
   beside the card. */
type CaseChapterProps = {
  view: CaseView;
  index: number;
  mode: StoryMode;
  frame?: string | null;
  /* LB-15 the slide is picked up and the camera is in on this card */
  selected?: boolean;
  /* LB-24 the scene is on this chapter */
  active?: boolean;
  onPreview: (view: CaseView) => void;
  onHost: (view: CaseView) => void;
  canHost: boolean;
  onLook?: (view: CaseView) => void;
  /* LB-51 the cases page: the same chapter as a row, the case file at
     reading size and its edges falling off into the room */
  compact?: boolean;
};

/* UI-DESIGN: LB-59 the case card on its own, so the three-case spread on
   the Home and the case chapter (the /games rows) share one card */
type CaseCardProps = {
  view: CaseView;
  mode: StoryMode;
  selected?: boolean;
  onPreview: (view: CaseView) => void;
  onHost: (view: CaseView) => void;
  canHost: boolean;
  onLook?: (view: CaseView) => void;
  className?: string;
  /* the heading id ties the card to its section (aria-labelledby) */
  headingId: string;
  /* LB-59/60 in the three-case row, Look closer is a quiet magnifier
     beside Preview (Avi: less prominence); the name stays for readers */
  lookIcon?: boolean;
};

export const CaseCard = ({ view, mode, selected = false, onPreview, onHost, canHost, onLook, className, headingId, lookIcon = false }: CaseCardProps) => {
  const product = productFor(view);
  const facts = [
    { label: "Players", value: view.players },
    { label: "Rounds", value: view.length },
    { label: "Difficulty", value: product.difficulty },
  ].filter((f): f is { label: string; value: string } => Boolean(f.value));
  const titleId = headingId;
  return (
    <GlassPanel className={clsx(styles.card, className)}>
      {/* UI-DESIGN: LB-30 the case file carries the number (slide label)
          and the name (folder tab), so the yellow Evidence marker is gone;
          the number stays in the heading for screen readers */}
      <h3 id={titleId} className={styles.name}>
        <span className={styles.visuallyHidden}>Case {view.number}: </span>
        {view.name}
      </h3>
      {/* UI-DESIGN: LB-85 use the public mystery description, not draft plot copy. */}
      {product.hook ? <p className={styles.tagline}>{product.hook}</p> : null}
      {/* UI-DESIGN: LB-34 what a host decides on is written on a real
          evidence tag (manila stock, reinforced eyelet, printed header,
          typed entries) instead of a mono label grid between hairlines.
          It carries no case number: the slide label already does. */}
      {facts.length ? (
        <div className={styles.tagWrap}>
          <div className={styles.tag}>
            <span className={styles.eyelet} aria-hidden />
            <dl className={styles.facts}>
              {facts.map((f) => (
                <div key={f.label} className={styles.fact}>
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      ) : null}
      <div className={clsx(styles.actions, lookIcon && styles.actionsRow)}>
        {/* UI-DESIGN: LB-77 (Taco 2026-10-16): two actions, trying the case
            first and a look at who you could play second; the first clue
            preview is retired from the card */}
        <LightboxButton tone="primary" isDisabled={!canHost} onPress={() => onHost(view)}>
          Try this case
        </LightboxButton>
        <LightboxButton tone="secondary" onPress={() => onPreview(view)}>
          Preview a role
        </LightboxButton>
        {/* UI-DESIGN: LB-15 the keyboard way to pick the slide up (the
            pointer way is clicking the slide); Escape puts it back.
            LB-48 a real button (Taco: the text link didn't read as
            clickable), the hero's secondary glass, a zoom-in magnifier */}
        {mode === "three" && onLook ? (
          <LightboxButton
            tone="secondary"
            icon={selected ? <Xmark /> : <MagnifierPlus />}
            aria-pressed={selected}
            data-lb-look=""
            className={lookIcon ? styles.lookIcon : undefined}
            onPress={() => onLook(view)}
          >
            {lookIcon ? (
              <span className={styles.visuallyHidden}>{selected ? `Put ${view.name} back` : `Look closer at ${view.name}`}</span>
            ) : selected ? "Put it back" : "Look closer"}
          </LightboxButton>
        ) : null}
      </div>
    </GlassPanel>
  );
};

const CaseChapter = ({ view, index, mode, frame, selected = false, active = false, onPreview, onHost, canHost, onLook, compact = false }: CaseChapterProps) => {
  const titleId = `lb-case-${view.number}-title`;
  return (
    <section
      id={`case-${view.number}`}
      className={clsx(styles.chapter, selected && styles.selected, index % 2 === 1 && styles.flip, compact && styles.compact)}
      data-lb-chapter="case"
      data-case-id={view.id}
      data-lb-active={active ? "" : undefined}
      aria-labelledby={titleId}
    >
      <div className={styles.pin} data-lb-pin="">
        <div className={styles.stage}>
          {mode !== "frames" ? <SlideMount view={view} className={styles.mount} /> : null}
          {mode === "frames" && frame ? (
            <img className={styles.frame} src={frame} alt="" loading="lazy" decoding="async" />
          ) : null}
          {mode === "frames" && !frame ? <SlideMount view={view} className={styles.mount} /> : null}
        </div>

        <CaseCard view={view} mode={mode} selected={selected} onPreview={onPreview} onHost={onHost} canHost={canHost} onLook={onLook} headingId={titleId} />
      </div>
    </section>
  );
};

export default CaseChapter;
